/**
 * Lavender Theme
 *
 * Calm, sophisticated purple tones.
 */

import type { AppTheme } from './types';

export const lavenderTheme: AppTheme = {
  name: 'lavender',
  displayName: 'Lavender',
  description: 'Calm, sophisticated purple tones',

  antdTheme: {
    token: {
      colorPrimary: '#7c3aed',
      colorSuccess: '#22c55e',
      colorWarning: '#f59e0b',
      colorError: '#ef4444',
      colorInfo: '#8b5cf6',
      colorLink: '#7c3aed',

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 8,
      borderRadiusLG: 10,
      borderRadiusSM: 6,
      borderRadiusXS: 4,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#faf5ff',
      colorBorder: '#d8b4fe',
      colorBorderSecondary: '#e9d5ff',

      colorText: '#4c1d95',
      colorTextSecondary: '#6b21a8',
      colorTextTertiary: '#c4b5fd',
      colorTextQuaternary: '#d8b4fe',

      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },

    components: {
      Layout: {
        headerBg: '#6d28d9',
        headerColor: '#ffffff',
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: '#4c1d95',
        bodyBg: '#faf5ff',
      },

      Button: {
        borderRadius: 8,
        borderRadiusLG: 10,
        borderRadiusSM: 6,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 10,
        borderRadiusLG: 12,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: '#faf5ff',
        headerColor: '#c4b5fd',
        headerBorderRadius: 8,
        borderRadius: 8,
        rowHoverBg: '#faf5ff',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Select: {
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Tag: {
        borderRadiusSM: 4,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 12,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: '#c4b5fd',
        linkColor: '#7c3aed',
      },

      Segmented: {
        borderRadius: 8,
        itemSelectedBg: '#7c3aed',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#c4b5fd',
        itemSelectedColor: '#4c1d95',
        itemHoverColor: '#6b21a8',
      },

      Dropdown: {
        borderRadiusLG: 8,
      },

      Tooltip: {
        borderRadius: 6,
      },

      Divider: {
        colorSplit: '#e9d5ff',
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': '#6d28d9',
    '--layout-sidebar-bg': '#4c1d95',
    '--layout-content-bg': '#faf5ff',

    '--surface-0': '#faf5ff',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    '--text-primary': '#4c1d95',
    '--text-secondary': '#6b21a8',
    '--text-tertiary': '#c4b5fd',
    '--text-muted': '#d8b4fe',

    '--border-default': '#d8b4fe',
    '--border-subtle': '#e9d5ff',
    '--border-strong': '#c4b5fd',

    '--border-color': '#d8b4fe',
    '--border-radius': '8px',

    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.03)',
    '--shadow-md': '0 1px 3px rgba(0, 0, 0, 0.04)',
    '--shadow-lg': '0 4px 16px rgba(0, 0, 0, 0.08)',

    '--accent-warm': '#f59e0b',
    '--accent-cool': '#8b5cf6',

    '--status-success': '#22c55e',
    '--status-warning': '#f59e0b',
    '--status-error': '#ef4444',
    '--status-info': '#8b5cf6',

    '--status-success-subtle': '#ecfdf5',
    '--status-warning-subtle': '#fffbeb',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#f5f3ff',

    '--control-bg': '#faf5ff',
    '--control-border': '#d8b4fe',
    '--control-hover': '#e9d5ff',

    '--hover-bg': '#faf5ff',
    '--active-bg': '#e9d5ff',
  },
};
